// Mirrors the Go backend's messaging/application/message_dto.go and the
// Postgres enums in internal/migrations/000014_create_messaging_tables.up.sql
export type MessageDirection = 'outbound' | 'inbound'
export type MessageStatus = 'queued' | 'sent' | 'failed' | 'received'
export type TemplateCategory = 'general' | 'customers' | 'service'
export type TemplateTrigger =
  | 'work_order_status_changed'
  | 'work_order_completed'
  | 'deal_stage_changed'
  | 'service_due'
  | 'registration_temp_tag_expiring'

export interface Message {
  id: string
  customer_id: string
  direction: MessageDirection
  status: MessageStatus
  body: string
  template_id: string | null
  deal_id: string | null
  work_order_id: string | null
  to_number: string
  twilio_sid: string
  error?: string
  created_at: string
  sent_at: string | null
}

export interface MessageTemplate {
  id: string
  name: string
  category: TemplateCategory
  body: string
  trigger_type: TemplateTrigger | null
  trigger_value: string
  created_at: string
  updated_at: string
}

export interface TemplateWriteBody {
  name: string
  category: TemplateCategory
  body: string
  trigger_type: TemplateTrigger | ''
  trigger_value: string
}

export interface Conversation {
  customer_id: string
  customer_name: string
  last_body: string
  last_direction: MessageDirection
  last_at: string
  message_count: number
}

export interface SendMessageBody {
  id?: string // client-generated: replay-idempotent send (backend skips Twilio on conflict)
  customer_id?: string
  deal_id?: string
  work_order_id?: string
  template_id?: string
  body?: string
}

// Available {{placeholders}} — must match messaging/domain/template.go.
export const TEMPLATE_VARIABLES = [
  'first_name',
  'last_name',
  'boat_name',
  'work_order_title',
  'deal_title',
  'service_title',
  'due_date',
] as const

export const TEMPLATE_CATEGORIES: { value: TemplateCategory; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'customers', label: 'Customers' },
  { value: 'service', label: 'Service' },
]

export const TEMPLATE_TRIGGERS: { value: TemplateTrigger; label: string }[] = [
  { value: 'work_order_status_changed', label: 'Work order status changed' },
  { value: 'work_order_completed', label: 'Work order completed' },
  { value: 'deal_stage_changed', label: 'Deal stage changed' },
  { value: 'service_due', label: 'Service due (owner reminder)' },
  { value: 'registration_temp_tag_expiring', label: 'Registration temp tag expiring' },
]

// Legal trigger_value filters per trigger (must match the corresponding
// backend enums: WorkOrderStatus and DealStage).
export const TRIGGER_VALUE_OPTIONS: Record<TemplateTrigger, { value: string; label: string }[]> = {
  work_order_status_changed: [
    { value: '', label: 'Any status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'on_hold', label: 'On hold' },
  ],
  work_order_completed: [{ value: '', label: 'On completion' }],
  deal_stage_changed: [
    { value: '', label: 'Any stage' },
    { value: 'prospecting', label: 'Prospecting' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
  ],
  service_due: [
    { value: '', label: 'Any due kind' },
    { value: 'time', label: 'Calendar due' },
    { value: 'hours', label: 'Engine hours due' },
  ],
  registration_temp_tag_expiring: [{ value: '', label: 'Any deadline' }],
}
